import { useEffect } from 'react';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://bayshoreofficial.directbooking.sg';

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Vela Bay – Official Direct Booking',
  url: siteUrl,
  description:
    'Official direct booking site for Vela Bay, 515 luxury sea-facing condominiums at Bayshore Road, District 16, Singapore by SingHaiyi Group.',
  inLanguage: 'en-SG',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${siteUrl}/?s={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Vela Bay | Bayshore Road D16 Singapore | Official Direct Booking',
  url: siteUrl,
  description:
    'Vela Bay at Bayshore Road, District 16 Singapore. 515 luxury condominiums by SingHaiyi Group. Sea-facing units, direct MRT access. Register for exclusive pricing and showflat appointments.',
  inLanguage: 'en-SG',
  isPartOf: { '@type': 'WebSite', url: siteUrl },
  about: {
    '@type': 'ApartmentComplex',
    name: 'Vela Bay',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Bayshore Road',
      addressLocality: 'Singapore',
      addressRegion: 'District 16',
      addressCountry: 'SG',
    },
  },
  dateModified: '2026-04-14',
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'SingHaiyi Group',
  alternateName: 'Haiyi Holdings',
  url: 'https://www.singhaiyi.com',
  logo: {
    '@type': 'ImageObject',
    url: 'https://www.singhaiyi.com/images/logo.png',
    width: 200,
    height: 60,
  },
  description:
    'SingHaiyi Group (Haiyi Holdings) is a reputable Singapore-listed real estate developer with a strong track record in residential and commercial developments across Singapore and internationally.',
  foundingLocation: {
    '@type': 'Place',
    name: 'Singapore',
  },
  areaServed: 'SG',
  knowsAbout: ['Luxury Condominiums', 'Real Estate Development', 'Singapore Property'],
  makesOffer: {
    '@type': 'Offer',
    itemOffered: {
      '@type': 'ApartmentComplex',
      name: 'Vela Bay',
      url: siteUrl,
    },
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+6593651286',
    contactType: 'sales',
    areaServed: 'SG',
    availableLanguage: ['English', 'Chinese'],
  },
  sameAs: [
    'https://www.singhaiyi.com',
    'https://en.wikipedia.org/wiki/SingHaiyi_Group',
  ],
};

const apartmentSchema = {
  '@context': 'https://schema.org',
  '@type': 'ApartmentComplex',
  name: 'Vela Bay',
  description:
    '515 luxury sea-facing condominiums at Bayshore Road, District 16, Singapore, developed by SingHaiyi Group. Direct MRT access via Bayshore MRT.',
  url: siteUrl,
  telephone: '+6593651286',
  image:
    'https://d3h1y9jvvpl9bt.cloudfront.net/assets/images/610009ab-605b-40b3-a303-4e68d624ef31/clean-shot-2026-03-08-at-16-49-38-2-x-1920w.webp',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Bayshore Road',
    addressLocality: 'Singapore',
    addressRegion: 'District 16',
    addressCountry: 'SG',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '1.313411',
    longitude: '103.940396',
  },
  numberOfRooms: 515,
  petsAllowed: false,
  amenityFeature: [
    { '@type': 'LocationFeatureSpecification', name: 'Sea View', value: true },
    { '@type': 'LocationFeatureSpecification', name: 'MRT Access', value: true },
    { '@type': 'LocationFeatureSpecification', name: 'East Coast Park Proximity', value: true },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is the address for Vela Bay?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Vela Bay is located along Bayshore Road, District 16, Singapore.',
      },
    },
    {
      '@type': 'Question',
      name: 'How many units are available at Vela Bay?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Vela Bay has a total of 515 residential units ranging from 1+Study to 5-bedroom configurations. More than 70% of units are sea-facing.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the price for Vela Bay?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Estimated guide pricing starts at approximately $2,783 psf++. Please register your interest to receive the latest official price list.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the expected TOP date for Vela Bay?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The Temporary Occupation Permit (TOP) date for Vela Bay will be announced closer to launch. Please register to stay updated.',
      },
    },
    {
      '@type': 'Question',
      name: 'Where can I find the showflat for Vela Bay?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The Vela Bay Sales Gallery is strictly by advance appointment only. Contact us at +65 9365 1286 to book your visit.',
      },
    },
    {
      '@type': 'Question',
      name: 'Who is the developer of Vela Bay?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Vela Bay is developed by SingHaiyi Group / Haiyi Holdings, a reputable developer with a strong track record in Singapore.',
      },
    },
  ],
};

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'RealEstateAgent',
  name: 'Vela Bay Sales Gallery',
  description:
    'Official sales gallery for Vela Bay, 515 luxury sea-facing condominiums at Bayshore Road, District 16, Singapore by SingHaiyi Group. Strictly by advance appointment only.',
  url: siteUrl,
  telephone: '+6593651286',
  image:
    'https://d3h1y9jvvpl9bt.cloudfront.net/assets/images/610009ab-605b-40b3-a303-4e68d624ef31/clean-shot-2026-03-08-at-16-49-38-2-x-1920w.webp',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Bayshore Road',
    addressLocality: 'Singapore',
    addressRegion: 'District 16',
    postalCode: '469983',
    addressCountry: 'SG',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '1.313411',
    longitude: '103.940396',
  },
  hasMap: 'https://maps.google.com/?q=1.313411,103.940396',
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '10:00',
      closes: '19:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Saturday', 'Sunday'],
      opens: '10:00',
      closes: '18:00',
    },
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+6593651286',
    contactType: 'sales',
    areaServed: 'SG',
    availableLanguage: ['English', 'Chinese'],
  },
  currenciesAccepted: 'SGD',
  priceRange: '$$$',
  areaServed: {
    '@type': 'Place',
    name: 'Singapore District 16 – Bayshore Road',
  },
  sameAs: [siteUrl],
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Vela Bay – Bayshore Road D16',
      item: siteUrl,
    },
  ],
};

const ALL_SCHEMAS = [
  { id: 'schema-website', data: websiteSchema },
  { id: 'schema-web-page', data: webPageSchema },
  { id: 'schema-organization', data: organizationSchema },
  { id: 'schema-local-business', data: localBusinessSchema },
  { id: 'schema-apartment-complex', data: apartmentSchema },
  { id: 'schema-faq-page', data: faqSchema },
  { id: 'schema-breadcrumb', data: breadcrumbSchema },
];

export default function SEOHead() {
  useEffect(() => {
    const inject = (id: string, data: object) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.text = JSON.stringify(data);
      document.head.appendChild(script);
    };

    ALL_SCHEMAS.forEach(({ id, data }) => inject(id, data));

    return () => {
      ALL_SCHEMAS.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return null;
}
